/**
 * Администрирование площадки (только роль admin; администратор не играет):
 *   «Обзор» — пользователи, розыгрыши, кредиты, столы, динамика по дням, лучшие игроки;
 *   «Пользователи» — поиск, фильтры, переход в профиль, блокировка и разблокировка;
 *   «Розыгрыши» — все розыгрыши с переходом к повтору.
 */
import { useEffect, useState } from 'react';
import type { AdminUserRow } from '@mt/protocol';
import { api, useLoad } from '../../lib/api';
import { cr, crn } from '../../lib/format';
import { Link, navigate } from '../../lib/router';
import { client, useApp } from '../../net/client';
import { ProviderIcon } from '../auth/ProviderIcon';
import { ago, Avatar, DayBars, Empty, Pager, pct, signedCr, Tile, toneOf } from '../kit';
import { GameList } from '../profile/GameList';

type Tab = 'overview' | 'users' | 'games';
const TABS: { id: Tab; title: string }[] = [
  { id: 'overview', title: 'Обзор' },
  { id: 'users', title: 'Пользователи' },
  { id: 'games', title: 'Розыгрыши' },
];

export function AdminPage() {
  const login = useApp((s) => s.adminLogin);
  const initial = new URLSearchParams(location.search).get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(initial && TABS.some((t) => t.id === initial) ? initial : 'overview');
  const choose = (t: Tab) => {
    setTab(t);
    history.replaceState(null, '', t === 'overview' ? '/admin' : `/admin?tab=${t}`);
  };
  return (
    <div className="page admin-page">
      <header className="page-head">
        <div>
          <h1>Администрирование</h1>
          <p className="muted-line">Вы вошли как администратор {login ? <b>{login}</b> : null}. Администратор не участвует в розыгрышах.</p>
        </div>
      </header>
      <div className="segmented tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''} onClick={() => choose(t.id)}>
            {t.title}
          </button>
        ))}
      </div>
      {tab === 'overview' && <Overview />}
      {tab === 'users' && <Users />}
      {tab === 'games' && (
        <section className="panel">
          <GameList mode="admin" load={(o, l) => api.admin.games(o, l)} replayPath={(id) => `/admin/games/${id}`} pageSize={30} />
        </section>
      )}
    </div>
  );
}

function Overview() {
  const { data: o, error, reload } = useLoad(() => api.admin.overview(), []);
  useEffect(() => {
    const id = setInterval(reload, 15_000);
    return () => clearInterval(id);
  }, [reload]);
  if (error) return <p className="notice danger">{error}</p>;
  if (!o) return <p className="muted-line">Загрузка…</p>;
  return (
    <div className="admin-overview">
      <section className="panel">
        <h3 className="panel-title">Пользователи</h3>
        <dl className="tiles">
          <Tile label="Всего" value={o.users.total} />
          <Tile label="В сети" value={o.users.online} tone={o.users.online ? 'up' : ''} />
          <Tile label="Новых за сутки" value={o.users.new24h} />
          <Tile label="Новых за неделю" value={o.users.new7d} />
          <Tile label="Играли за неделю" value={o.users.active7d} />
          <Tile label="Заблокировано" value={o.users.banned} tone={o.users.banned ? 'down' : ''} />
          <Tile label="С паролем" value={o.users.withPassword} />
          <Tile label="Через сервисы" value={o.users.withSso} />
        </dl>
      </section>
      <section className="panel">
        <h3 className="panel-title">Розыгрыши</h3>
        <dl className="tiles">
          <Tile label="Всего" value={o.games.total} />
          <Tile label="За сутки" value={o.games.last24h} />
          <Tile label="Состоялось" value={o.games.completed} />
          <Tile label="Отменено" value={o.games.cancelled} />
          <Tile label="Игроков в среднем" value={o.games.avgPlayers ?? '—'} />
          <Tile label="Доля людей" value={pct(o.games.humanShare)} hint="люди среди всех участников (остальные — боты)" />
        </dl>
      </section>
      <section className="panel">
        <h3 className="panel-title">Кредиты</h3>
        <dl className="tiles">
          <Tile label="Счёт банка" value={crn(o.credits.bank)} hint="накопленная комиссия" />
          <Tile label="Комиссия всего" value={crn(o.credits.commissionTotal)} />
          <Tile label="Выдано игрокам" value={crn(o.credits.granted)} hint="стартовые и восстановленные балансы" />
          <Tile label="Сейчас в банках" value={crn(o.credits.inPlay)} hint="внесено в незавершённые розыгрыши" />
          <Tile label="Оборот банков" value={crn(o.credits.potTotal)} />
        </dl>
      </section>
      <section className="panel">
        <h3 className="panel-title">Сейчас</h3>
        <dl className="tiles">
          <Tile label="Открыто столов" value={o.rooms.open} />
          <Tile label="Идёт розыгрышей" value={o.rooms.playing} />
          <Tile label="Людей за столами" value={o.rooms.seated} />
          <Tile label="Наблюдателей" value={o.rooms.spectators} />
        </dl>
      </section>
      <section className="panel wide">
        <h3 className="panel-title">Розыгрыши по дням</h3>
        <DayBars items={o.daily} value={(i) => o.daily[i]!.games} label="Розыгрыши по дням" signed={false} format={(v) => `${v} розыгрышей`} />
        <h3 className="panel-title">Комиссия по дням</h3>
        <DayBars items={o.daily} value={(i) => o.daily[i]!.commission} label="Комиссия по дням" signed={false} format={(v) => cr(v)} />
      </section>
      <section className="panel">
        <h3 className="panel-title">По столам</h3>
        {o.byCategory.length === 0 ? (
          <Empty>Розыгрышей пока не было.</Empty>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Взнос</th>
                <th className="num">Розыгрышей</th>
                <th className="num">Оборот</th>
                <th className="num">Комиссия</th>
              </tr>
            </thead>
            <tbody>
              {o.byCategory.map((c) => (
                <tr key={c.categoryId}>
                  <td data-label="Взнос">{cr(c.ante)}</td>
                  <td data-label="Розыгрышей" className="num mono">{c.games}</td>
                  <td data-label="Оборот" className="num mono">{crn(c.pot)}</td>
                  <td data-label="Комиссия" className="num mono">{crn(c.commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="panel">
        <h3 className="panel-title">Лучшие игроки</h3>
        {o.topPlayers.length === 0 ? (
          <Empty>Пока никто не играл.</Empty>
        ) : (
          <ol className="top-list">
            {o.topPlayers.map((p) => (
              <li key={p.id}>
                <Link to={`/admin/users/${p.id}`}>{p.name}</Link>
                <span className="dim small">
                  {p.games} игр · {pct(p.winRate)}
                </span>
                <b className={`mono ${toneOf(p.net)}`}>{signedCr(p.net)}</b>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function Users() {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'online' | 'banned'>('all');
  const [offset, setOffset] = useState(0);
  const { data, error, reload } = useLoad(() => api.admin.users(query, status, offset), [query, status, offset]);
  useEffect(() => {
    const id = setTimeout(() => (setQuery(q.trim()), setOffset(0)), 300);
    return () => clearTimeout(id);
  }, [q]);
  const toggleBan = async (u: AdminUserRow) => {
    try {
      if (u.bannedAt) await api.admin.unban(u.id);
      else {
        const reason = prompt(`Причина блокировки игрока ${u.name} (видна игроку):`, '');
        if (reason === null) return;
        await api.admin.ban(u.id, reason);
      }
      reload();
    } catch (e) {
      client.toast('error', (e as Error).message);
    }
  };
  return (
    <section className="panel">
      <div className="toolbar">
        <input className="search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск по имени, логину или id" aria-label="Поиск пользователей" />
        <div className="segmented three">
          {(['all', 'online', 'banned'] as const).map((s) => (
            <button key={s} className={status === s ? 'on' : ''} onClick={() => (setStatus(s), setOffset(0))}>
              {s === 'all' ? 'Все' : s === 'online' ? 'В сети' : 'Заблокированные'}
            </button>
          ))}
        </div>
      </div>
      {error && <p className="notice danger">{error}</p>}
      {data && data.total === 0 && <Empty>Никого не найдено.</Empty>}
      {data && data.total > 0 && (
        <>
          <div className="table-scroll">
            <table className="data-table users-table">
              <thead>
                <tr>
                  <th>Игрок</th>
                  <th>Вход</th>
                  <th>Активность</th>
                  <th className="num">Игр</th>
                  <th className="num">Винрейт</th>
                  <th className="num">Итог</th>
                  <th className="num">Баланс</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <tr key={u.id} className={u.bannedAt ? 'banned' : ''}>
                    <td data-label="Игрок">
                      <span className="user-cell">
                        <Avatar id={u.id} name={u.name} size={28} />
                        <span>
                          <Link to={`/admin/users/${u.id}`}>{u.name}</Link>
                          {u.online && <em className="tag ok">в сети</em>}
                          {u.bannedAt && <em className="tag danger" title={u.banReason ?? ''}>заблокирован</em>}
                        </span>
                      </span>
                    </td>
                    <td data-label="Вход">
                      <span className="methods">
                        {u.login && <span className="chip-static">{u.login}</span>}
                        {u.providers.map((p) => (
                          <ProviderIcon key={p} id={p} />
                        ))}
                        {!u.login && u.providers.length === 0 && <span className="dim small">только сессия</span>}
                      </span>
                    </td>
                    <td data-label="Активность" className="small">
                      {u.online ? 'сейчас' : ago(u.lastSeenAt)}
                    </td>
                    <td data-label="Игр" className="num mono">{u.games}</td>
                    <td data-label="Винрейт" className="num mono">{pct(u.winRate)}</td>
                    <td data-label="Итог" className={`num mono ${toneOf(u.net)}`}>{signedCr(u.net)}</td>
                    <td data-label="Баланс" className="num mono">{crn(u.balance)}</td>
                    <td className="num row-action">
                      <span className="row-actions">
                        <button className="btn tiny ghost" onClick={() => navigate(`/admin/users/${u.id}`)}>
                          Профиль
                        </button>
                        <button className={`btn tiny ${u.bannedAt ? '' : 'danger-outline'}`} onClick={() => void toggleBan(u)}>
                          {u.bannedAt ? 'Разблокировать' : 'Заблокировать'}
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={data.offset} limit={data.limit} total={data.total} onPage={setOffset} />
        </>
      )}
    </section>
  );
}
